import React, { useState } from 'react';
import api from '../api';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { validatePassword, PASSWORD_HINT_NEW } from '../passwordPolicy';
import LegalFooter from '../components/LegalFooter';
import PasswordInput from '../components/PasswordInput';
import { DPA_VERSION, DPA_TEXT } from '../dpaText';

const LICENCE_VERSION = 'v1.0';

const LICENCE_TEXT = `GOLF SOCIETY MANAGER
Copyright & Software Licence Notice
Version 1.0 | © 2026 Michael Sellman. All Rights Reserved.

1. OWNERSHIP
The software known as Golf Society Manager, including all associated source code, web applications (React/Node.js/PostgreSQL), Windows Desktop/Electron applications, Java/JavaFX interfaces, scoring engines, algorithms, UI designs and documentation is the intellectual property of Michael Sellman and is protected under United Kingdom and international copyright laws.

2. COPYRIGHT
© 2026 Michael Sellman — All Rights Reserved.
No part of this software may be copied, reproduced, modified, reverse engineered, distributed, sold, sublicensed, or incorporated into another product without prior written permission.

3. SOFTWARE SCOPE
Web Application: React frontend on golfsocietymanager.com, Node.js/Express API, PostgreSQL multi-tenant database, Nginx, PM2, Let's Encrypt SSL.
Windows Desktop: Java/JavaFX applications, Electron wrapper, SQLite database, installer packages.

4. TECHNOLOGIES & TOOLS USED
Frontend: React 19, React Router v7, Tailwind CSS v3, Axios.
Backend: Node.js v20, Express.js, PostgreSQL, bcryptjs, jsonwebtoken, dotenvx.
Infrastructure: Ubuntu Server 24.04 LTS, Nginx, PM2, Let's Encrypt/Certbot.
Desktop/Legacy: Java, JavaFX/FXML, Electron, SQLite, Apache Maven, Microsoft Excel/VBA.
AI Development Assistance: Claude (Anthropic) — used for code generation, architecture design, database schema, debugging and documentation. All intellectual property, design decisions and creative direction remain solely with Michael Sellman.

5. PROTECTED FEATURES
Golf competition management workflows; Stableford, Medal, Match Play, Fourball and Skins scoring; grouping algorithms; buggy allocation logic; leaderboard and countback calculations; Twos & Fines tracking; archive and player statistics; multi-tenant society data isolation; scorecard layouts; UI screen designs; database schema and relationships; Copyright & Licence acceptance workflow.

6. PERMITTED USE
Licensed users may install and use the software for golf society administration, enter competition data, generate reports and maintain local backups of their own society data, provided use is in accordance with the licence granted by the copyright owner.

7. RESTRICTIONS
Users may NOT resell, sublicense or commercially exploit the software; remove copyright notices; redistribute modified versions; decompile or reverse engineer the application; use the software to create competing products; or publish source code online — without explicit written permission from Michael Sellman.

8. LICENCE ACCEPTANCE & VERSION CONTROL
Upon registration, users must accept this Licence Agreement. The following is recorded: user name, email address, date and time of acceptance, and licence version accepted. If terms are updated, users will be required to re-accept before continuing.

9. NO WARRANTY
This software is provided AS IS without warranty of any kind. The copyright owner accepts no liability for data loss, scoring errors, competition disputes, financial losses or software conflicts arising from use of the software.

10. COMMERCIAL USE
Commercial use, resale, white-labelling, hosting services or redistribution is prohibited unless authorised in writing by Michael Sellman.

11. ENFORCEMENT
Unauthorised copying, distribution or modification may result in licence termination, legal action, financial claims and copyright enforcement proceedings under applicable intellectual property laws.

12. CONTACT
Michael Sellman | Golf Society Manager | United Kingdom | golfsocietymanager.com

© 2026 Michael Sellman. Golf Society Manager. All Rights Reserved.`;

export default function Register() {
  const [searchParams] = useSearchParams();
  const isClub = searchParams.get('type') === 'club';
  const [form, setForm] = useState({ name: '', email: '', password: '', address_line1: '', address_line2: '', city: '', county: '', postcode: '', country: 'United Kingdom' });
  const [acceptLicence, setAcceptLicence] = useState(false);
  const [acceptNoRedist, setAcceptNoRedist] = useState(false);
  const [showLicence, setShowLicence] = useState(false);
  const [acceptDpa, setAcceptDpa] = useState(false);
  const [showDpa, setShowDpa] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const canSubmit = form.name && form.email && form.password && acceptLicence && acceptNoRedist && acceptDpa
    && (!isClub || (form.address_line1 && form.city && form.postcode && form.country));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const passwordError = validatePassword(form.password, 12);
    if (passwordError) return setError(passwordError);
    setLoading(true);
    try {
      const payload = { ...form, licence_version: LICENCE_VERSION, licence_accepted_at: new Date().toISOString(), dpa_version: DPA_VERSION, dpa_accepted_at: new Date().toISOString() };
      if (isClub) payload.account_type = 'club';
      const res = await api.post('/auth/register', payload);
      if (isClub) { setSubmitted(true); return; }
      localStorage.setItem('landingDest', '/players');
      window.location.href = '/players';
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Registration failed');
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center py-8">
        <div className="bg-white p-8 rounded-xl shadow-md w-full max-w-md text-center">
          <div className="text-4xl mb-3">✅</div>
          <h1 className="text-xl font-bold text-amber-800 mb-3">Registration submitted</h1>
          <p className="text-sm text-gray-600 leading-relaxed">
            Thanks — your club registration is now awaiting approval. We check every new club before activating its account, and you'll receive an email as soon as it's been reviewed.
          </p>
          <button onClick={() => navigate('/choose-login')} className="mt-5 border border-gray-300 rounded-lg px-5 py-2 text-sm text-gray-600 hover:bg-gray-50">
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={isClub ? "min-h-screen bg-amber-50 flex items-center justify-center py-8" : "min-h-screen bg-green-50 flex items-center justify-center py-8"}>
      <div className="bg-white p-8 rounded-xl shadow-md w-full max-w-md">
        <h1 className={isClub ? "text-3xl font-bold text-amber-700 mb-1 text-center" : "text-3xl font-bold text-green-700 mb-1 text-center"}>{isClub ? '📍' : '⛳'} Golf Society Manager</h1>
        <h2 className="text-xl font-semibold text-gray-600 mb-2 text-center">{isClub ? 'Club Sign-Up' : 'Organiser Sign-Up'}</h2>
        {isClub ? (
          <p className="text-xs text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-6">
            This registers your golf club itself (the venue), separate from any society that plays there. New registrations are reviewed before activation.
          </p>
        ) : (
          <p className="text-xs text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-6">
            This creates a brand-new golf society as its own account. Already play for a society that uses GSM?
            You don't need this — <Link to="/player/register" className="underline font-semibold">register as a player instead</Link>.
          </p>
        )}
        {error && <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-4 text-sm">{error}</div>}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{isClub ? 'Club Name' : "Your Golf Society's Name"}</label>
            <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} autoComplete="off" placeholder={isClub ? 'e.g. Hagley Golf And Country Club' : 'e.g. Pin High Golf Society'} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} autoComplete="off" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <PasswordInput value={form.password} onChange={e => setForm({...form, password: e.target.value})} autoComplete="new-password" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            <p className="text-sm font-medium text-gray-600 mt-1">{PASSWORD_HINT_NEW}</p>
          </div>
          {isClub && (
            <div className="border-t border-gray-200 pt-4 space-y-3">
              <label className="block text-xs font-semibold text-amber-800 uppercase tracking-wide">Club Address</label>
              <input type="text" value={form.address_line1} onChange={e => setForm({...form, address_line1: e.target.value})} placeholder="Address Line 1" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <input type="text" value={form.address_line2} onChange={e => setForm({...form, address_line2: e.target.value})} placeholder="Address Line 2 (optional)" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <input type="text" value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="Town / City" className="w-1/2 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input type="text" value={form.county} onChange={e => setForm({...form, county: e.target.value})} placeholder="County" className="w-1/2 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex gap-2">
                <input type="text" value={form.postcode} onChange={e => setForm({...form, postcode: e.target.value})} placeholder="Postcode" className="w-1/2 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <input type="text" value={form.country} onChange={e => setForm({...form, country: e.target.value})} placeholder="Country" className="w-1/2 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
          )}
        </div>
        <div className="border border-gray-300 rounded-lg p-4 mb-6 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Licence Agreement</h3>
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={acceptLicence} onChange={e => setAcceptLicence(e.target.checked)} className="mt-0.5 w-4 h-4 accent-green-600" />
              <span className="text-sm text-gray-700">I have read and accept the Golf Society Manager Copyright & Licence Agreement</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={acceptNoRedist} onChange={e => setAcceptNoRedist(e.target.checked)} className="mt-0.5 w-4 h-4 accent-green-600" />
              <span className="text-sm text-gray-700">I agree not to copy, redistribute, reverse engineer or resell Golf Society Manager software</span>
            </label>
          </div>
          <button onClick={() => setShowLicence(true)} className="mt-3 text-sm text-green-600 hover:text-green-800 underline font-medium">View Full Licence Agreement</button>
        </div>
        <div className="border border-gray-300 rounded-lg p-4 mb-6 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Data Processing Agreement</h3>
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={acceptDpa} onChange={e => setAcceptDpa(e.target.checked)} className="mt-0.5 w-4 h-4 accent-green-600" />
              <span className="text-sm text-gray-700">I have read and accept the Golf Society Manager Data Processing Agreement, covering how member personal data is handled on our behalf</span>
            </label>
          </div>
          <button onClick={() => setShowDpa(true)} className="mt-3 text-sm text-green-600 hover:text-green-800 underline font-medium">View Full Data Processing Agreement</button>
        </div>
        <button onClick={handleSubmit} disabled={!canSubmit || loading}
          className={`w-full font-semibold py-2 px-4 rounded-lg transition text-white text-sm ${canSubmit && !loading ? 'bg-green-600 hover:bg-green-700 cursor-pointer' : 'bg-gray-300 cursor-not-allowed'}`}>
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
        <p className="text-center text-sm text-gray-500 mt-4">Already have an account? <Link to="/login" className="text-green-600 hover:underline">Sign in</Link></p>
        <div className="mt-6"><LegalFooter /></div>
      </div>

      {showLicence && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl flex flex-col" style={{maxHeight:'90vh'}}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Golf Society Manager — Licence Agreement v1.0</h2>
              <button onClick={() => setShowLicence(false)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">&times;</button>
            </div>
            <div className="overflow-auto px-6 py-4 flex-1">
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{LICENCE_TEXT}</pre>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
              <span className="text-xs text-gray-500">© 2026 Michael Sellman. All Rights Reserved.</span>
              <button onClick={() => { setAcceptLicence(true); setAcceptNoRedist(true); setShowLicence(false); }}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-lg text-sm">Accept & Close</button>
            </div>
          </div>
        </div>
      )}

      {showDpa && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl flex flex-col" style={{maxHeight:'90vh'}}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-800">Golf Society Manager — Data Processing Agreement {DPA_VERSION}</h2>
              <button onClick={() => setShowDpa(false)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">&times;</button>
            </div>
            <div className="overflow-auto px-6 py-4 flex-1">
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{DPA_TEXT}</pre>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
              <span className="text-xs text-gray-500">© 2026 Michael Sellman. All Rights Reserved.</span>
              <button onClick={() => { setAcceptDpa(true); setShowDpa(false); }}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-lg text-sm">Accept & Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
